import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { FilterCriteria } from './marketingSegments';

export interface MarketingCampaign {
  id: number;
  name: string;
  status: 'draft' | 'codes_generated' | 'active' | 'completed';
  total_codes: number;
  sent_codes: number;
  redeemed_codes: number;
  expires_at: string | null;
  created_at: string;
  brevo_list_id: number | null;
  offer: { name: string; offer_type: string; package_code: string } | null;
  segment: { name: string; estimated_count: number | null } | null;
}

export interface OfferCode {
  id: number;
  code: string;
  assigned_user_id: string;
  assigned_email: string;
  status: 'pending' | 'sent' | 'redeemed' | 'expired';
  sent_at: string | null;
  redeemed_at: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface CampaignCodesPage {
  codes: OfferCode[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

function useInvalidateCampaigns() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['marketing-campaigns'] });
}

export function useMarketingCampaigns() {
  return useQuery({
    queryKey: ['marketing-campaigns'],
    queryFn: async () => (await apiClient<{ campaigns: MarketingCampaign[] }>('/marketing/campaigns')).campaigns,
  });
}

export function useCreateMarketingCampaign() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: async (draft: { name: string; offer_id: number; segment_id?: number; expires_at?: string }) =>
      (await apiClient<{ campaign: MarketingCampaign }>('/marketing/campaigns', { method: 'POST', body: JSON.stringify(draft) })).campaign,
    onSuccess: invalidate,
  });
}

export function useCampaignCodes(campaignId: number, params: { page: number; limit?: number; status?: string; search?: string }) {
  const query = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit ?? 50),
    ...(params.status ? { status: params.status } : {}),
    ...(params.search ? { search: params.search } : {}),
  });
  return useQuery({
    queryKey: ['marketing-campaign-codes', campaignId, params],
    queryFn: () => apiClient<CampaignCodesPage>(`/marketing/campaigns/${campaignId}/codes?${query.toString()}`),
  });
}

export function useGenerateCampaignCodes() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (campaignId: number) =>
      apiClient<{ generated: number; skipped: number; total: number }>(`/marketing/campaigns/${campaignId}/generate-codes`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

// The list is spent once the codes have gone out; removing it keeps the Brevo
// account from filling with one list per campaign. Contacts are untouched.
export function useDeleteCampaignBrevoList() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (id: number) =>
      apiClient<{ success: boolean; deletedListId: number }>(`/marketing/campaigns/${id}/brevo-list`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function usePushCampaignToBrevo() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (campaignId: number) =>
      apiClient<{ pushed: number; skippedWithoutConsent: number; brevoListId: number; brevoListName: string }>(`/marketing/campaigns/${campaignId}/push-to-brevo`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export function useMarkCampaignCodesSent() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (campaignId: number) =>
      apiClient<{ marked_sent: number }>(`/marketing/campaigns/${campaignId}/codes/mark-sent`, { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: invalidate,
  });
}

// One-step campaign builder: the discount, segment, offer and campaign in a
// single call, optionally minting the codes, pushing them to Brevo and sending.
export interface CampaignBuildRequest {
  name: string;
  expires_at?: string;
  audience: { segment_id?: number; conditions?: FilterCriteria; manual_user_ids?: string[] };
  reward:
    | { type: 'FREE_GRANT'; package_code: string; access_duration_days: number }
    | { type: 'STRIPE_DISCOUNT'; discount_id: number }
    | { type: 'STRIPE_DISCOUNT'; percent_off: number; duration?: 'once' | 'repeating' | 'forever'; duration_in_months?: number };
  launch?: { email_campaign_id: number; confirm_send?: boolean };
}

export interface CampaignPreview {
  matched: number;
  deliverable: number;
  undeliverable: number;
  sample: string[];
  reward: string;
  creates: string[];
  blockers: string[];
}

export interface CampaignBuildResult {
  campaign: MarketingCampaign;
  created: { discountId?: number; discountCode?: string; segmentId?: number; offerId: number };
  launched?: { generated: number; unresolved: number; pushed: number; skippedWithoutConsent: number; brevoListId: number; sent: boolean; promoCodesMarkedSent: number };
}

export function usePreviewCampaignBuild() {
  return useMutation({
    mutationFn: async (request: CampaignBuildRequest) =>
      (await apiClient<{ preview: CampaignPreview }>('/marketing/campaigns/build', {
        method: 'POST',
        body: JSON.stringify({ ...request, preview: true }),
      })).preview,
  });
}

export function useBuildCampaign() {
  const invalidate = useInvalidateCampaigns();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CampaignBuildRequest) =>
      apiClient<CampaignBuildResult>('/marketing/campaigns/build', { method: 'POST', body: JSON.stringify(request) }),
    onSuccess: () => {
      invalidate();
      // A build can create a discount, a segment and an offer, and a launch
      // sends a marketing email - every list that shows them is now stale.
      ['marketing-segments', 'marketing-offers', 'discounts', 'marketing-campaigns-email', 'brevo-lists']
        .forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
    },
  });
}
